import type { DocumentRef } from '../../lib/documentReader'
import type { DatedBalanceView, InstrumentDateResultView } from './InstrumentDateResult'

export interface InstrumentDateRequest {
  selected_reference_id: string
  replacement_date: string | null
  as_of: string
}
interface InstrumentDatedBalance {
  value: string
  denomination: string | null
  currency: string | null
  financial_date: string | null
}
export interface InstrumentDateReference extends InstrumentDatedBalance {
  id: string
  receipt_id?: string
  is_current?: boolean
  source_documents?: DocumentRef[]
  available?: boolean
  pending_reasons?: string[]
  replaces_reference_id?: string
}
interface DateProjection {
  value: string | null
  denomination: string | null
  currency: string | null
  reference_date: string | null
  value_date: string | null
}
interface DateComparison {
  reference_id: string
  previous_reference_id: string | null
  status: 'first_reference' | 'balanced' | 'break' | 'unknown'
  expected: string | null
  stated: string
  delta: string | null
  findings: string[]
}
export interface InstrumentDatePlan {
  request: InstrumentDateRequest
  selected_before: InstrumentDateReference | null
  requested_reference: InstrumentDateReference | null
  before_current_reference: InstrumentDateReference | null
  resulting_current_reference: InstrumentDateReference | null
  before_anchor: InstrumentDatedBalance | null
  resulting_anchor: InstrumentDatedBalance | null
  calculated_before: DateProjection
  calculated_after: DateProjection | null
  intervals_before: DateComparison[]
  intervals_after: DateComparison[]
  references: InstrumentDateReference[]
  documents: DocumentRef[]
  pending_reasons: string[]
  can_apply: boolean
  snapshot_fingerprint: string
  plan_fingerprint: string
  binding_revision: string
}

export function sameDateRequest(
  left: InstrumentDateRequest,
  right: InstrumentDateRequest
): boolean {
  return (
    left.selected_reference_id === right.selected_reference_id &&
    left.replacement_date === right.replacement_date &&
    left.as_of === right.as_of
  )
}
function balance(value: InstrumentDatedBalance | null): DatedBalanceView | null {
  return value
    ? {
        value: value.value,
        financialDate: value.financial_date,
        denomination: value.denomination,
        currency: value.currency,
      }
    : null
}
function projection(value: DateProjection | null): DatedBalanceView | null {
  return value
    ? {
        value: value.value,
        financialDate: value.value_date,
        denomination: value.denomination,
        currency: value.currency,
      }
    : null
}
/** Rendering adaptation only; the browser does not calculate or approve balances. */
export function dateResultView(plan: InstrumentDatePlan): InstrumentDateResultView {
  const before = new Map(plan.references.map((reference) => [reference.id, reference]))
  if (plan.selected_before) before.set(plan.selected_before.id, plan.selected_before)
  const after = new Map(before)
  if (plan.requested_reference) after.set(plan.requested_reference.id, plan.requested_reference)
  const intervals = (rows: DateComparison[], references: Map<string, InstrumentDateReference>) =>
    rows.map((row) => ({
      key: row.reference_id,
      from: row.previous_reference_id
        ? (references.get(row.previous_reference_id)?.financial_date ?? null)
        : null,
      to: references.get(row.reference_id)?.financial_date ?? null,
      status: row.status,
      expected: row.expected,
      stated: row.stated,
      difference: row.delta,
      findings: row.findings,
    }))
  return {
    asOf: plan.request.as_of,
    selectedBefore: balance(plan.selected_before),
    replacement: balance(plan.requested_reference),
    beforeCurrentReference: balance(plan.before_current_reference),
    resultingCurrentReference: balance(plan.resulting_current_reference),
    beforeAnchor: balance(plan.before_anchor),
    resultingAnchor: balance(plan.resulting_anchor),
    calculatedBefore: projection(plan.calculated_before),
    calculatedAfter: projection(plan.calculated_after),
    intervalsBefore: intervals(plan.intervals_before, before),
    intervalsAfter: intervals(plan.intervals_after, after),
    reasons: plan.pending_reasons,
    documents: plan.documents,
  }
}
